"use client";

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent,
} from "react";
import {
  motion,
  useInView,
  useMotionValue,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
  type MotionStyle,
  type Variants,
} from "framer-motion";
import { stegaClean } from "@sanity/client/stega";
import type { PagePricingSchema } from "@/sanity/pages/PagePricingTemplate";
import { DeferredPaint, Reveal, SectionHeading } from "../ai-features/ui";
import styles from "./pricing.module.scss";

type BillingCycle = "monthly" | "yearly";

/**
 * Same notch cut-out as the home pricing card: an objectBoundingBox clipPath
 * with two semicircles punched out of the card edges at the tear row.
 * Uses offset geometry (layout only) so the hover tilt never skews the math.
 */
function useNotchClip() {
  const cardRef = useRef<HTMLElement>(null);
  const tearRef = useRef<HTMLDivElement>(null);
  const clipId = `notch-clip-${useId().replace(/:/g, "")}`;
  const [pathD, setPathD] = useState("");

  useEffect(() => {
    const compute = () => {
      const card = cardRef.current;
      const tear = tearRef.current;
      if (!card || !tear) return;
      const W = card.offsetWidth;
      const H = card.offsetHeight;
      if (W === 0 || H === 0) return;
      const cy = (tear.offsetTop + tear.offsetHeight / 2) / H;
      const rx = tear.offsetHeight / 2 / W;
      const ry = tear.offsetHeight / 2 / H;
      setPathD(
        [
          "M 0 0 L 1 0 L 1 1 L 0 1 Z",
          `M 0 ${cy - ry} A ${rx} ${ry} 0 0 1 0 ${cy + ry} Z`,
          `M 1 ${cy - ry} A ${rx} ${ry} 0 0 0 1 ${cy + ry} Z`,
        ].join(" "),
      );
    };
    const raf = requestAnimationFrame(compute);
    const observer = new ResizeObserver(compute);
    if (cardRef.current) observer.observe(cardRef.current);
    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, []);

  return { cardRef, tearRef, clipId, pathD };
}

const TILT_DEG = 5;
const spring = { stiffness: 180, damping: 22, mass: 0.6 };

// Ghost copies of the card (desktop only): same move as the home pricing
// card. Rest geometry comes from Figma: ±396px at 1920, scale 0.97, +24px.
const ghostSpring = { type: "spring", stiffness: 80, damping: 24, mass: 0.6 };
const ghostVariants = (direction: -1 | 1): Variants => ({
  hidden: { x: 0, y: "3rem", scale: 1, opacity: 0 },
  shown: {
    x: `${direction * 49.6}rem`,
    y: "3rem",
    scale: 0.97,
    opacity: 0.4,
    transition: {
      ...ghostSpring,
      delay: 0.55,
      opacity: { duration: 0.35, delay: 0.55 },
    },
  },
});

/** Pointer-following 3D tilt, fine pointers only, off under reduced motion. */
function useCardTilt(enabled: boolean) {
  const rotateX = useSpring(useMotionValue(0), spring);
  const rotateY = useSpring(useMotionValue(0), spring);
  const glowX = useSpring(useMotionValue(50), spring);
  const glowY = useSpring(useMotionValue(50), spring);

  const onPointerMove = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (!enabled || event.pointerType !== "mouse") return;
      const rect = event.currentTarget.getBoundingClientRect();
      const px = (event.clientX - rect.left) / rect.width;
      const py = (event.clientY - rect.top) / rect.height;
      rotateY.set((px - 0.5) * 2 * TILT_DEG);
      rotateX.set((0.5 - py) * 2 * TILT_DEG);
      glowX.set(px * 100);
      glowY.set(py * 100);
    },
    [enabled, rotateX, rotateY, glowX, glowY],
  );
  const onPointerLeave = useCallback(() => {
    rotateX.set(0);
    rotateY.set(0);
    glowX.set(50);
    glowY.set(50);
  }, [rotateX, rotateY, glowX, glowY]);

  return { rotateX, rotateY, glowX, glowY, onPointerMove, onPointerLeave };
}

export default function HeroSection({
  data,
}: {
  data?: PagePricingSchema["hero"];
}) {
  const [cycle, setCycle] = useState<BillingCycle>("monthly");
  const reducedMotion = useReducedMotion();
  const { cardRef, tearRef, clipId, pathD } = useNotchClip();
  const tilt = useCardTilt(!reducedMotion);
  const stageRef = useRef<HTMLDivElement>(null);
  const stageInView = useInView(stageRef, { once: true, amount: 0.3 });
  // Scroll-linked spread: the first 500px of page scroll push the ghosts a
  // further 8rem outward and shrink them a touch, springed like the home card.
  const { scrollY } = useScroll();
  const spread = useSpring(useTransform(scrollY, [0, 500], [0, 1]), {
    stiffness: 80,
    damping: 24,
    mass: 0.6,
  });
  const spreadLeft = useTransform(spread, (v) => `${-v * 8}rem`);
  const spreadRight = useTransform(spread, (v) => `${v * 8}rem`);
  const spreadScale = useTransform(spread, [0, 1], [1, 0.96]);
  if (!data) return null;
  const price = cycle === "monthly" ? data.monthlyPrice : data.yearlyPrice;
  const card = data.card;
  return (
    <section className={styles.hero}>
      <span className={styles.heroGlow} aria-hidden="true" />
      <DeferredPaint className={styles.heroNoise} />
      <div className={styles.heroCopy}>
        <SectionHeading data={data} />
        {(data.monthlyLabel || data.yearlyLabel) && (
          <div className={styles.billing}>
            <span data-active={cycle === "monthly"}>{data.monthlyLabel}</span>
            <button
              type="button"
              role="switch"
              aria-checked={cycle === "yearly"}
              aria-label="Toggle billing cycle"
              className={styles.toggle}
              data-cycle={cycle}
              onClick={() =>
                setCycle((prev) => (prev === "monthly" ? "yearly" : "monthly"))
              }
            >
              <i />
              <b />
            </button>
            <span data-active={cycle === "yearly"}>{data.yearlyLabel}</span>
          </div>
        )}
      </div>
      {card && (
        <Reveal className={styles.cardWrap}>
          <div
            ref={stageRef}
            className={styles.cardStage}
            data-ghosts={!reducedMotion}
          >
            {pathD && (
              <svg width="0" height="0" className={styles.cardClipDefs}>
                <defs>
                  <clipPath id={clipId} clipPathUnits="objectBoundingBox">
                    <path d={pathD} clipRule="evenodd" />
                  </clipPath>
                </defs>
              </svg>
            )}
            {([-1, 1] as const).map((direction) => (
              <motion.div
                key={direction}
                className={styles.ghost}
                aria-hidden="true"
                variants={ghostVariants(direction)}
                initial={reducedMotion ? "shown" : "hidden"}
                animate={stageInView || reducedMotion ? "shown" : "hidden"}
              >
                <motion.div
                  style={
                    reducedMotion
                      ? undefined
                      : {
                          x: direction < 0 ? spreadLeft : spreadRight,
                          scale: spreadScale,
                        }
                  }
                >
                <article
                  className={styles.card}
                  style={pathD ? { clipPath: `url(#${clipId})` } : undefined}
                >
                  <span className={styles.cardGlow} aria-hidden="true" />
                  <header className={styles.cardHead}>
                    <i aria-hidden="true" />
                    <span>{card.title}</span>
                    <span>{card.subtitle}</span>
                  </header>
                  <div className={styles.cardPrice}>
                    <strong>TradeStats</strong>
                  </div>
                  <svg
                    className={styles.cardMark}
                    viewBox="0 0 198.843 213.945"
                    aria-hidden="true"
                  >
                    <path
                      d="M131.962 0V70.0242L65.9885 0H0V71.9604H130.163L65.0813 144.897V213.945H131.962V71.9604H198.843V0H131.962Z"
                      fill="none"
                      stroke="#040404"
                      strokeWidth="1.26"
                    />
                  </svg>
                  <div className={styles.cardTear} aria-hidden="true" />
                  <ul className={styles.cardFeatures}>
                    {stegaClean(card.features ?? []).map((feature, i) => (
                      <li key={i}>
                        <i aria-hidden="true" className={styles.cardDot} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </article>
                </motion.div>
              </motion.div>
            ))}
            <motion.div
              className={styles.cardShell}
              style={{
                rotateX: tilt.rotateX,
                rotateY: tilt.rotateY,
                transformPerspective: 1600,
              }}
              onPointerMove={tilt.onPointerMove}
              onPointerLeave={tilt.onPointerLeave}
            >
              <article
                ref={cardRef}
                className={styles.card}
                style={pathD ? { clipPath: `url(#${clipId})` } : undefined}
              >
                <span className={styles.cardGlow} aria-hidden="true" />
                <DeferredPaint className={styles.cardNoise} />
                <motion.span
                  className={styles.cardSheen}
                  aria-hidden="true"
                  style={
                    {
                      "--sheen-x": tilt.glowX,
                      "--sheen-y": tilt.glowY,
                    } as MotionStyle
                  }
                />
                <header className={styles.cardHead}>
                  <i aria-hidden="true" />
                  <span>{card.title}</span>
                  <span>{card.subtitle}</span>
                </header>
                <div className={styles.cardPrice} data-cycle={cycle}>
                  <strong key={cycle}>{price?.price}</strong>
                  {cycle === "yearly" && price?.discounted && (
                    <s key={`${cycle}-was`}>{price.discounted}</s>
                  )}
                </div>
                <svg
                  className={styles.cardMark}
                  viewBox="0 0 198.843 213.945"
                  aria-hidden="true"
                >
                  <path
                    d="M131.962 0V70.0242L65.9885 0H0V71.9604H130.163L65.0813 144.897V213.945H131.962V71.9604H198.843V0H131.962Z"
                    fill="none"
                    stroke="#040404"
                    strokeWidth="1.26"
                  />
                </svg>
                <div
                  ref={tearRef}
                  className={styles.cardTear}
                  aria-hidden="true"
                />
                <ul className={styles.cardFeatures}>
                  {stegaClean(card.features ?? []).map((feature, i) => (
                    <li key={i} style={{ "--i": i } as CSSProperties}>
                      <i aria-hidden="true" className={styles.cardDot} />
                      {feature}
                    </li>
                  ))}
                </ul>
              </article>
            </motion.div>
          </div>
        </Reveal>
      )}
    </section>
  );
}
